import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { USER_API } from "config";
import { parseDocumentCommentsListResponse } from "features/script-editor/parseDocumentCommentsListResponse";
import {
  COMMENT_LIST_POLL_MS,
  fetchAllDocumentComments,
} from "features/script-editor/fetchAllDocumentComments";
import { useAxios } from "hooks/useAxios";
import { scriptKeys } from "queries";
import { scheduleKeys } from "queries/schedule.keys";
import { projectCommentsKeys } from "queries/projectComments.keys";
import type { CommentSceneSummaryItem, CreateCommentPayload, DocumentComment } from "../types";

async function invalidateDocumentCommentsQueries(
  queryClient: QueryClient,
  projectId: string,
  documentId: string
) {
  await queryClient.invalidateQueries({
    queryKey: projectCommentsKeys.commentsDocumentScope(projectId, documentId),
  });
  await queryClient.invalidateQueries({
    queryKey: scriptKeys.commentsDocumentScope(projectId, documentId),
  });
  await queryClient.invalidateQueries({
    queryKey: scheduleKeys.commentsDocumentScope(projectId, documentId),
  });
  await queryClient.invalidateQueries({
    queryKey: scriptKeys.commentsSummary(projectId, documentId),
  });
}

/**
 * A 404 on a comment action means someone else already removed it (or its thread).
 * Refetch so the stale thread disappears instead of failing again on the next click.
 */
function refetchCommentsIfGone(
  queryClient: QueryClient,
  error: unknown,
  variables: { projectId: string; documentId: string }
): void {
  if (isAxiosError(error) && error.response?.status === 404) {
    void invalidateDocumentCommentsQueries(queryClient, variables.projectId, variables.documentId);
  }
}

function documentCommentsListScopes(projectId: string, documentId: string) {
  return [
    projectCommentsKeys.commentsDocumentScope(projectId, documentId),
    scriptKeys.commentsDocumentScope(projectId, documentId),
    scheduleKeys.commentsDocumentScope(projectId, documentId),
  ];
}

function patchDocumentCommentsListCache(
  queryClient: QueryClient,
  projectId: string,
  documentId: string,
  commentId: string,
  patch: (comment: DocumentComment) => DocumentComment
): void {
  for (const queryKey of documentCommentsListScopes(projectId, documentId)) {
    queryClient.setQueriesData<DocumentComment[]>({ queryKey }, (existing) => {
      const list = parseDocumentCommentsListResponse(existing);
      if (list.length === 0) return list;

      let changed = false;
      const next = list.map((comment) => {
        if (comment.comment_id !== commentId) return comment;
        changed = true;
        return patch(comment);
      });

      return changed ? next : list;
    });
  }
}

/** Prepend a newly created comment so margin/panel update before refetch completes. */
export function insertDocumentCommentIntoListCaches(
  queryClient: QueryClient,
  projectId: string,
  documentId: string,
  created: DocumentComment
): void {
  if (!created?.comment_id) return;

  for (const queryKey of documentCommentsListScopes(projectId, documentId)) {
    queryClient.setQueriesData<DocumentComment[]>({ queryKey }, (existing) => {
      const list = parseDocumentCommentsListResponse(existing);
      if (list.some((comment) => comment.comment_id === created.comment_id)) {
        return list;
      }
      return [created, ...list];
    });
  }
}

export type MarkCommentThreadReadParams = {
  projectId: string;
  documentId: string;
  rootCommentId: string;
};

export type CreateCommentParams = {
  projectId: string;
  documentId: string;
  payload: CreateCommentPayload;
};

export type ResolveCommentParams = {
  projectId: string;
  documentId: string;
  commentId: string;
};

export type UpdateCommentParams = {
  projectId: string;
  documentId: string;
  commentId: string;
  body: string;
};

export function useCommentSceneSummary(projectId: string, documentId: string, enabled: boolean) {
  const axios = useAxios();

  return useQuery<CommentSceneSummaryItem[]>({
    enabled,
    queryKey: scriptKeys.commentsSummary(projectId, documentId),
    queryFn: async () => {
      const body = await axios.get(USER_API.getDocumentCommentsSummary(projectId, documentId));
      const typed = body as { data: { scenes: CommentSceneSummaryItem[] } };
      return typed.data.scenes;
    },
    staleTime: 30 * 1000,
  });
}

export function useCommentThreadReads(projectId: string, documentId: string, enabled: boolean) {
  const axios = useAxios();

  return useQuery<Record<string, string>>({
    enabled,
    queryKey: scriptKeys.commentThreadReads(projectId, documentId),
    queryFn: async () => {
      const body = (await axios.get(
        USER_API.getDocumentCommentThreadReads(projectId, documentId)
      )) as { data: { reads: Record<string, string> } };
      return body.data.reads ?? {};
    },
    staleTime: 60 * 1000,
  });
}

type MarkCommentThreadReadCtx = {
  previousReads: Record<string, string> | undefined;
};

export function useMarkCommentThreadRead() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      documentId,
      rootCommentId,
    }: MarkCommentThreadReadParams): Promise<{ root_comment_id: string; read_at: string }> => {
      const body = (await axios.put(
        USER_API.documentCommentThreadReadForRoot(projectId, documentId, rootCommentId)
      )) as { data: { root_comment_id: string; read_at: string } };
      return body.data;
    },
    onMutate: async (variables): Promise<MarkCommentThreadReadCtx> => {
      const key = scriptKeys.commentThreadReads(variables.projectId, variables.documentId);
      await queryClient.cancelQueries({ queryKey: key });
      const previousReads = queryClient.getQueryData<Record<string, string>>(key);
      const optimisticIso = new Date().toISOString();
      queryClient.setQueryData(key, (prev: Record<string, string> | undefined) => ({
        ...(prev ?? {}),
        [variables.rootCommentId]: optimisticIso,
      }));
      return { previousReads };
    },
    onError: (_err, variables, context) => {
      const key = scriptKeys.commentThreadReads(variables.projectId, variables.documentId);
      queryClient.setQueryData(key, context?.previousReads);
    },
    onSuccess: (data, variables) => {
      queryClient.setQueryData(
        scriptKeys.commentThreadReads(variables.projectId, variables.documentId),
        (prev: Record<string, string> | undefined) => ({
          ...(prev ?? {}),
          [data.root_comment_id]: data.read_at,
        })
      );
    },
  });
}

export function useClearCommentThreadRead() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      documentId,
      rootCommentId,
    }: MarkCommentThreadReadParams): Promise<void> => {
      await axios.delete(
        USER_API.documentCommentThreadReadForRoot(projectId, documentId, rootCommentId)
      );
    },
    onMutate: async (variables): Promise<MarkCommentThreadReadCtx> => {
      const key = scriptKeys.commentThreadReads(variables.projectId, variables.documentId);
      await queryClient.cancelQueries({ queryKey: key });
      const previousReads = queryClient.getQueryData<Record<string, string>>(key);
      queryClient.setQueryData(key, (prev: Record<string, string> | undefined) => {
        const next = { ...(prev ?? {}) };
        delete next[variables.rootCommentId];
        return next;
      });
      return { previousReads };
    },
    onError: (_err, variables, context) => {
      const key = scriptKeys.commentThreadReads(variables.projectId, variables.documentId);
      queryClient.setQueryData(key, context?.previousReads);
    },
  });
}

export function useDocumentComments(
  projectId: string,
  documentId: string,
  enabled: boolean,
  filters: { sceneId?: number; status?: "open" | "resolved" } = {}
) {
  const axios = useAxios();

  return useQuery<DocumentComment[]>({
    enabled,
    queryKey: scriptKeys.comments(projectId, documentId, filters),
    queryFn: async () => {
      const params: Record<string, string | number> = {
        feature: "screenplay",
      };
      if (filters.sceneId != null) {
        params.scene_id = filters.sceneId;
      }
      if (filters.status != null) {
        params.status = filters.status;
      }
      return fetchAllDocumentComments(
        (pageParams) =>
          axios.get(USER_API.getDocumentComments(projectId, documentId), { params: pageParams }),
        params
      );
    },
    staleTime: 30 * 1000,
    // Picks up collaborators' comments; React Query pauses this in hidden tabs.
    refetchInterval: enabled ? COMMENT_LIST_POLL_MS : false,
  });
}

export function useCreateComment() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      documentId,
      payload,
    }: CreateCommentParams): Promise<DocumentComment> => {
      const { data } = await axios.post(
        USER_API.createDocumentComment(projectId, documentId),
        payload
      );
      return data;
    },
    onSuccess: async (created, variables) => {
      if (created?.comment_id) {
        insertDocumentCommentIntoListCaches(
          queryClient,
          variables.projectId,
          variables.documentId,
          created
        );
      }
      await invalidateDocumentCommentsQueries(
        queryClient,
        variables.projectId,
        variables.documentId
      );
    },
    // A reply to a thread that was just deleted returns 404.
    onError: (error, variables) => refetchCommentsIfGone(queryClient, error, variables),
  });
}

/** The server marks the thread read for whoever resolves or reopens it. */
function invalidateThreadReadsAfterStatusChange(
  queryClient: QueryClient,
  variables: { projectId: string; documentId: string }
) {
  void queryClient.invalidateQueries({
    queryKey: scriptKeys.commentThreadReads(variables.projectId, variables.documentId),
  });
}

export function useResolveComment() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      documentId,
      commentId,
    }: ResolveCommentParams): Promise<DocumentComment> => {
      const { data } = await axios.patch(
        USER_API.resolveDocumentComment(projectId, documentId, commentId)
      );
      return data;
    },
    onSuccess: (updatedComment, variables) => {
      patchDocumentCommentsListCache(
        queryClient,
        variables.projectId,
        variables.documentId,
        updatedComment.comment_id,
        (comment) => ({ ...comment, ...updatedComment })
      );
      void invalidateDocumentCommentsQueries(
        queryClient,
        variables.projectId,
        variables.documentId
      );
      invalidateThreadReadsAfterStatusChange(queryClient, variables);
    },
    onError: (error, variables) => refetchCommentsIfGone(queryClient, error, variables),
  });
}

export function useReopenComment() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      documentId,
      commentId,
    }: ResolveCommentParams): Promise<DocumentComment> => {
      const { data } = await axios.patch(
        USER_API.reopenDocumentComment(projectId, documentId, commentId)
      );
      return data;
    },
    onSuccess: (updatedComment, variables) => {
      patchDocumentCommentsListCache(
        queryClient,
        variables.projectId,
        variables.documentId,
        updatedComment.comment_id,
        (comment) => ({ ...comment, ...updatedComment })
      );
      void invalidateDocumentCommentsQueries(
        queryClient,
        variables.projectId,
        variables.documentId
      );
      invalidateThreadReadsAfterStatusChange(queryClient, variables);
    },
    onError: (error, variables) => refetchCommentsIfGone(queryClient, error, variables),
  });
}

export function useUpdateComment() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      documentId,
      commentId,
      body,
    }: UpdateCommentParams): Promise<DocumentComment> => {
      const { data } = await axios.patch(
        USER_API.documentCommentById(projectId, documentId, commentId),
        { body }
      );
      return data;
    },
    onSuccess: async (_data, variables) => {
      await invalidateDocumentCommentsQueries(
        queryClient,
        variables.projectId,
        variables.documentId
      );
    },
    onError: (error, variables) => refetchCommentsIfGone(queryClient, error, variables),
  });
}

export function useDeleteComment() {
  const axios = useAxios();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      projectId,
      documentId,
      commentId,
    }: ResolveCommentParams): Promise<void> => {
      await axios.delete(USER_API.documentCommentById(projectId, documentId, commentId));
    },
    onSuccess: async (_data, variables) => {
      await invalidateDocumentCommentsQueries(
        queryClient,
        variables.projectId,
        variables.documentId
      );
      await queryClient.invalidateQueries({
        queryKey: scriptKeys.commentThreadReads(variables.projectId, variables.documentId),
      });
    },
    onError: (error, variables) => refetchCommentsIfGone(queryClient, error, variables),
  });
}
